import React, { useState, useEffect, useCallback } from 'react';
import {
  MantineProvider,
  Container,
  Title,
  Text,
  Button,
  Loader,
  Group,
  createTheme,
  rem,
  Alert,
  Box
} from '@mantine/core';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import { IconBell, IconBellOff, IconCheck, IconX } from '@tabler/icons-react';
import '@mantine/core/styles.css';

// Función para generar o recuperar un ID de usuario único del almacenamiento local.
// En una aplicación real, este ID provendría de tu sistema de autenticación.
const getUserId = () => {
  const userId = "ABC123"
  return userId;
};

// URL de tu backend. Asegúrate de que coincida con la dirección donde se ejecuta tu servidor Node.js.
// Se lee de las variables de entorno de React (REACT_APP_BACKEND_URL).
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Clave pública VAPID generada para tu aplicación.
// Se lee de las variables de entorno de React (REACT_APP_VAPID_PUBLIC_KEY).
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || 'TU_CLAVE_PUBLICA_VAPID_AQUI';

// Define un tema personalizado para Mantine.
const theme = createTheme({
  fontFamily: 'Inter, sans-serif',
  headings: {
    fontFamily: 'Inter, sans-serif',
    sizes: {
      h1: { fontSize: rem(36) },
    },
  },
  colors: {
    brand: ['#E0F7FA', '#B3E5FC', '#81D4FA', '#4FC3F7', '#29B6F6', '#03A9F4', '#039BE5', '#0288D1', '#0277BD', '#01579B'],
  },
  primaryColor: 'brand',
});

// Componente principal de la página de inicio.
function HomePage() {
  const [isPushSupported, setIsPushSupported] = useState(false); // Indica si el navegador soporta notificaciones push.
  const [isSubscribed, setIsSubscribed] = useState(false);     // Indica si el usuario está suscrito a las notificaciones.
  const [loading, setLoading] = useState(true);               // Estado de carga para operaciones asíncronas.
  const [permissionStatus, setPermissionStatus] = useState(Notification.permission); // Permiso actual de notificaciones.
  const [message, setMessage] = useState(null);               // Mensajes para mostrar al usuario (éxito/error/info).
  const userId = getUserId();                                 // ID único del usuario.

  // Función utilitaria para convertir una cadena base64 URL-safe a un Uint8Array.
  // Necesaria para la clave pública VAPID.
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/\-/g, '+')
      .replace(/_/g, '/');
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);
    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Verifica el estado de la suscripción push y los permisos del navegador.
  const checkSubscriptionStatus = useCallback(async () => {
    setLoading(true);
    if ('serviceWorker' in navigator && 'PushManager' in window) {
      setIsPushSupported(true);
      setPermissionStatus(Notification.permission); // Actualiza el estado del permiso

      try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
          setIsSubscribed(true);
          console.log('El usuario ya está suscrito a notificaciones push:', subscription);
        } else {
          setIsSubscribed(false);
          console.log('El usuario NO está suscrito a notificaciones push.');
        }
      } catch (error) {
        console.error('Error al obtener la suscripción:', error);
        setIsSubscribed(false);
      }
    } else {
      setIsPushSupported(false);
    }
    setLoading(false);
  }, []);

  // Efecto que se ejecuta al montar el componente para registrar el Service Worker
  // y verificar el estado inicial de la suscripción.
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/service-worker.js')
        .then(registration => console.log('Service Worker registrado con éxito:', registration))
        .catch(error => console.error('Fallo el registro del Service Worker:', error));
    }

    checkSubscriptionStatus();
  }, [checkSubscriptionStatus]);

  // Maneja el proceso de suscripción a las notificaciones push.
  const handleSubscribe = async () => {
    setLoading(true);
    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission); // Actualiza el estado del permiso después de la solicitud.
console.log("Vapid: ",VAPID_PUBLIC_KEY)
      if (permission === 'granted') {
        const registration = await navigator.serviceWorker.ready;
        const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
        const subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true, // Esto es requerido por las especificaciones push.
          applicationServerKey: applicationServerKey
        });

        console.log('Suscripción generada por el navegador:', subscription);

        // Envía el objeto de suscripción y el ID de usuario a tu backend.
        const response = await fetch(`${BACKEND_URL}/api/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ userId, subscription }),
        });

        if (response.ok) {
          setMessage({ type: 'success', text: '¡Te has suscrito a las notificaciones con éxito!' });
          setIsSubscribed(true);
        } else {
          const errorData = await response.json();
          setMessage({ type: 'error', text: `Error al guardar la suscripción en el servidor: ${errorData.message}` });
          setIsSubscribed(false);
        }
      } else {
        setMessage({ type: 'error', text: 'Permiso de notificación denegado.' });
        setIsSubscribed(false);
      }
    } catch (error) {
      console.error('Error al suscribirse:', error);
      setMessage({ type: 'error', text: `Error al suscribirse a las notificaciones: ${error.message}` });
      setIsSubscribed(false);
    } finally {
      setLoading(false);
    }
  };

  // Maneja el proceso de desuscripción de las notificaciones push.
  const handleUnsubscribe = async () => {
    setLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        await subscription.unsubscribe(); // Desuscribe del navegador.
        console.log('Suscripción eliminada del navegador.');

        // Opcional: Aquí deberías notificar a tu backend para que elimine esta suscripción
        // de tu base de datos también. Esto es crucial para mantener tu base de datos limpia.
        // Ejemplo (requeriría un endpoint DELETE en tu backend):
        // await fetch(`${BACKEND_URL}/api/unsubscribe/${userId}`, { method: 'DELETE' });

        setMessage({ type: 'success', text: 'Te has desuscrito de las notificaciones.' });
        setIsSubscribed(false);
        setPermissionStatus('default'); // Reinicia el estado del permiso para una posible resuscripción.
      } else {
        setMessage({ type: 'info', text: 'No estás suscrito actualmente.' });
      }
    } catch (error) {
      console.error('Error al desuscribirse:', error);
      setMessage({ type: 'error', text: `Error al desuscribirse de las notificaciones: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  // Envía una notificación de prueba a este usuario desde el backend.
  // Útil para verificar que la suscripción funciona correctamente.
  const sendTestNotification = async () => {
    if (!isSubscribed) {
      setMessage({ type: 'info', text: 'Debes estar suscrito para enviar una notificación de prueba.' });
      return;
    }
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/send-notification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          title: 'Notificación de Prueba',
          body: `¡Hola desde tu PWA! (ID: ${userId.substring(0, 8)}...)`,
          icon: 'https://cdn.doyturnos.com.ar/web/favicon.ico', // Asegúrate de que este icono exista en tu carpeta public.
          url: 'https://doyturnos.com' // URL de ejemplo para redirección al hacer clic.
        }),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Notificación de prueba enviada a tu dispositivo.' });
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: `Error al enviar notificación de prueba: ${errorData.message}` });
      }
    } catch (error) {
      console.error('Error al enviar notificación de prueba:', error);
      setMessage({ type: 'error', text: `Error de red al enviar notificación de prueba: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container size="sm" py="xl" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
      <Box p="md" shadow="md" radius="md" withBorder>
        <Title order={1} ta="center" mb="md" c="brand.6">
          Notificaciones Push PWA 🔔
        </Title>
        <Text size="lg" ta="center" mb="xl">
          Tu ID de Usuario: <Text span fw={700}>{userId}</Text>
        </Text>

        {loading ? (
          <Group justify="center">
            <Loader color="brand" />
            <Text>Cargando estado...</Text>
          </Group>
        ) : (
          <>
            {!isPushSupported ? (
              <Alert variant="filled" color="red" title="Notificaciones no soportadas" icon={<IconX />} mb="md">
                Tu navegador no soporta la API de Push.
              </Alert>
            ) : (
              <>
                {/* Mensajes de feedback para el usuario */}
                {message && (
                  <Alert
                    variant="light"
                    color={message.type === 'success' ? 'green' : (message.type === 'error' ? 'red' : 'blue')}
                    title={message.type === 'success' ? 'Éxito' : (message.type === 'error' ? 'Error' : 'Información')}
                    icon={message.type === 'success' ? <IconCheck /> : <IconX />}
                    mb="md"
                    onClose={() => setMessage(null)} // Permite cerrar el mensaje
                    withCloseButton
                  >
                    {message.text}
                  </Alert>
                )}

                {/* Advertencia si el permiso fue denegado */}
                {permissionStatus === 'denied' && (
                  <Alert variant="filled" color="red" title="Permiso denegado" icon={<IconX />} mb="md">
                    Has denegado los permisos de notificación. Por favor, habilítalos en la configuración de tu navegador.
                  </Alert>
                )}

                {/* Estado de la suscripción */}
                {isSubscribed ? (
                  <Alert variant="filled" color="green" title="Suscrito" icon={<IconCheck />} mb="md">
                    ¡Estás suscrito a las notificaciones! 🎉
                  </Alert>
                ) : (
                  <Alert variant="filled" color="yellow" title="No suscrito" icon={<IconX />} mb="md">
                    No estás suscrito a las notificaciones.
                  </Alert>
                )}

                {/* Botones de acción */}
                <Group justify="center" mt="xl">
                  {/* Botón Suscribirme: solo se muestra si NO está suscrito y el permiso NO ha sido denegado */}
                  {!isSubscribed && permissionStatus !== 'denied' && (
                    <Button
                      size="lg"
                      radius="md"
                      leftSection={<IconBell />}
                      onClick={handleSubscribe}
                      loading={loading}
                      variant="filled"
                      color="brand"
                    >
                      Suscribirme
                    </Button>
                  )}

                  {/* Botón Desuscribirme: solo se muestra si está suscrito */}
                  {isSubscribed && (
                    <Button
                      size="lg"
                      radius="md"
                      leftSection={<IconBellOff />}
                      onClick={handleUnsubscribe}
                      loading={loading}
                      variant="outline"
                      color="red"
                    >
                      Desuscribirme
                    </Button>
                  )}

                  {/* Botón Enviar Notificación de Prueba: solo se muestra si está suscrito */}
                  {isSubscribed && (
                    <Button
                      size="lg"
                      radius="md"
                      leftSection={<IconBell />}
                      onClick={sendTestNotification}
                      loading={loading}
                      variant="light"
                      color="blue"
                    >
                      Enviar Notificación de Prueba
                    </Button>
                  )}
                </Group>
              </>
            )}
          </>
        )}
      </Box>
    </Container>
  );
}

// Componente App que configura Mantine y React Router.
export default function App() {
  return (
    <MantineProvider theme={theme}>
      <Router>
        <Routes>
          <Route path="/" element={<HomePage />} />
          {/* Puedes añadir más rutas aquí si lo necesitas */}
        </Routes>
      </Router>
    </MantineProvider>
  );
}
